// The Period report (CONTEXT.md: "Period report") — Diary meals and Pain
// entries over a chosen date range, read straight from the global rows, so
// every date in the range counts whether or not a week existed for it.

import { addDays } from "./days";
import { type DiaryMeal, type DiaryMealsData, mealsByTime } from "./diary";
import { type PainEntryData, SYMPTOM_CATALOG, type Symptom } from "./pain";

export interface ReportDay {
  date: string;
  pain: PainEntryData | null;
  meals: DiaryMeal[]; // by time
}

export interface ReportSummary {
  dayCount: number;
  // A meal or a Pain entry with anything in it.
  loggedDays: number;
  levelDays: number;
  averageLevel: number | null;
  // Days per Symptom, catalog order, every symptom listed.
  symptomDays: { symptom: Symptom; label: string; days: number }[];
}

/** Every date from `from` to `to` inclusive; empty when from > to. */
export function datesBetween(from: string, to: string): string[] {
  const dates: string[] = [];
  for (let date = from; date <= to; date = addDays(date, 1)) {
    dates.push(date);
  }
  return dates;
}

function hasContent(pain: PainEntryData | null): boolean {
  return (
    pain !== null &&
    (typeof pain.level === "number" ||
      pain.symptoms.length > 0 ||
      (pain.note ?? "").length > 0)
  );
}

/**
 * One entry per date in the range. A date counts as logged when it has a
 * meal or a Pain entry with something in it — an entry emptied again by
 * clearing every field is not a logged day.
 */
export function reportDays(
  dates: string[],
  painEntries: PainEntryData[],
  mealDays: DiaryMealsData[],
): ReportDay[] {
  const painByDate = new Map(painEntries.map((e) => [e.date, e]));
  const mealsByDate = new Map(mealDays.map((d) => [d.date, d.meals]));
  return dates.map((date) => {
    const pain = painByDate.get(date) ?? null;
    return {
      date,
      pain: hasContent(pain) ? pain : null,
      meals: mealsByTime(mealsByDate.get(date) ?? []),
    };
  });
}

export function isLogged(day: ReportDay): boolean {
  return day.pain !== null || day.meals.length > 0;
}

export function summarizeReport(days: ReportDay[]): ReportSummary {
  const levels = days
    .map((d) => d.pain?.level)
    .filter((level): level is number => typeof level === "number");
  return {
    dayCount: days.length,
    loggedDays: days.filter(isLogged).length,
    levelDays: levels.length,
    averageLevel:
      levels.length > 0
        ? levels.reduce((sum, level) => sum + level, 0) / levels.length
        : null,
    symptomDays: SYMPTOM_CATALOG.map((s) => ({
      symptom: s.key,
      label: s.label,
      days: days.filter((d) => d.pain?.symptoms.includes(s.key)).length,
    })),
  };
}

/** «torsdag 14. mai 2026» for a YYYY-MM-DD date. */
export function formatReportDate(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("nb-NO", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}
